import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  PageTitle,
  Screen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import { CustomMap } from "~/features/maps/CustomMap";
import { GeoMap } from "~/features/maps/GeoMap";
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
} from "~/features/maps/map-styles";
import { useSignedUrl } from "~/features/media/storage";
import { useCollection, useCollectionActions } from "~/state/collections";
import { usePlantActions, usePlants } from "~/state/plants";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export default function CollectionMapScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const collection = useCollection(id);
  const plants = usePlants(id);
  const plantActions = usePlantActions();
  const collectionActions = useCollectionActions();
  const [placingId, setPlacingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const mapImage = useSignedUrl(
    "collection-maps",
    collection?.map_kind === "custom" ? collection.custom_map_path : null,
  );

  if (!collection || collection.map_kind === "none") {
    return (
      <Screen>
        <PageTitle title="Map" />
        <EmptyState
          title="This collection has no map"
          description="Turn on a map from the collection settings to start placing plants."
        />
      </Screen>
    );
  }

  const isGeo = collection.map_kind === "geo";
  const placed = plants.filter((plant) =>
    isGeo ? plant.map_lat !== null : plant.map_x !== null,
  );
  const unplaced = plants.filter((plant) =>
    isGeo ? plant.map_lat === null : plant.map_x === null,
  );
  const placingPlant = plants.find((plant) => plant.id === placingId) ?? null;

  return (
    <Screen>
      <Stack gap="md">
        <PageTitle
          title={`${collection.name} map`}
          subtitle={
            placingPlant
              ? `Tap the map to place ${placingPlant.display_name}.`
              : "Tap a plant below to place it, or tap a pin to open it."
          }
        />

        <View style={stylesheet.mapFrame}>
          {isGeo ? (
            <GeoMap
              style={collection.map_style}
              center={{
                lat: collection.map_center_lat ?? DEFAULT_MAP_CENTER.lat,
                lng: collection.map_center_lng ?? DEFAULT_MAP_CENTER.lng,
              }}
              zoom={collection.map_zoom ?? DEFAULT_MAP_ZOOM}
              placing={Boolean(placingId)}
              markers={placed.map((plant) => ({
                id: plant.id,
                label: plant.display_name,
                lat: plant.map_lat as number,
                lng: plant.map_lng as number,
                selected: plant.id === selectedId,
              }))}
              onPressMap={(coordinate) => {
                if (!placingId) return;
                plantActions.place(placingId, {
                  kind: "geo",
                  lat: coordinate.lat,
                  lng: coordinate.lng,
                });
                setSelectedId(placingId);
                setPlacingId(null);
              }}
              onPressMarker={setSelectedId}
              onRegionChange={(region) =>
                collectionActions.configureMap(collection.id, {
                  mapKind: "geo",
                  centerLat: region.lat,
                  centerLng: region.lng,
                  zoom: region.zoom,
                })
              }
            />
          ) : mapImage.data ? (
            <CustomMap
              imageUri={mapImage.data}
              aspectRatio={
                collection.custom_map_width && collection.custom_map_height
                  ? collection.custom_map_width / collection.custom_map_height
                  : undefined
              }
              placing={Boolean(placingId)}
              markers={placed.map((plant) => ({
                id: plant.id,
                label: plant.display_name,
                x: plant.map_x as number,
                y: plant.map_y as number,
                selected: plant.id === selectedId,
              }))}
              onPressMap={(point) => {
                if (!placingId) return;
                plantActions.place(placingId, {
                  kind: "custom",
                  x: point.x,
                  y: point.y,
                });
                setSelectedId(placingId);
                setPlacingId(null);
              }}
              onPressMarker={setSelectedId}
            />
          ) : (
            <View style={stylesheet.mapPlaceholder}>
              <AppText size="xs">
                {mapImage.isLoading
                  ? "Loading your map..."
                  : "Map image unavailable offline."}
              </AppText>
            </View>
          )}
        </View>

        {placingPlant ? (
          <Button
            variant="outline"
            align="start"
            onPress={() => setPlacingId(null)}
          >
            <ButtonText>Cancel placing {placingPlant.display_name}</ButtonText>
          </Button>
        ) : null}

        <Card gap="md">
          <SectionHeader
            title="Not on the map yet"
            description={`${unplaced.length} of ${plants.length} plants still need a position.`}
          />
          {unplaced.length === 0 ? (
            <EmptyState
              title="Everything is placed"
              description="Every plant in this collection has a spot on the map."
            />
          ) : (
            <Stack gap="sm">
              {unplaced.map((plant) => (
                <ListRow
                  key={plant.id}
                  title={plant.display_name}
                  onPress={() => setPlacingId(plant.id)}
                  meta={<Badge tone="neutral">{plant.status}</Badge>}
                  trailing={
                    <Button
                      compact
                      variant="outline"
                      iconName="map-pin"
                      onPress={() => setPlacingId(plant.id)}
                    >
                      <ButtonText>Place</ButtonText>
                    </Button>
                  }
                />
              ))}
            </Stack>
          )}
        </Card>

        {selectedId ? (
          <Card gap="sm">
            <SectionHeader title="Selected plant" />
            <ListRow
              title={
                plants.find((plant) => plant.id === selectedId)?.display_name ??
                "Plant"
              }
              trailing={
                <>
                  <Button
                    compact
                    variant="outline"
                    onPress={() => {
                      plantActions.place(selectedId, { kind: "clear" });
                      setSelectedId(null);
                    }}
                  >
                    <ButtonText>Remove pin</ButtonText>
                  </Button>
                  <Button
                    compact
                    iconName="external-link"
                    onPress={() =>
                      router.push(`/plants/${selectedId}` as never)
                    }
                  >
                    <ButtonText>Open</ButtonText>
                  </Button>
                </>
              }
            />
          </Card>
        ) : null}
      </Stack>
    </Screen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    mapFrame: {
      height: 420,
      overflow: "hidden",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.card,
      backgroundColor: colors.surface,
    },
    mapPlaceholder: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
    },
  });
